import { useMutation, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  CheckableList,
  CheckableListItem,
  CurrencyAmountInput,
  CurrencyAmountUI,
  PrimaryFullScreenSpinner,
  Spinner,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import { currencySchema, dateSchema, decimalSchema, now } from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Duration } from "luxon";
import { Suspense, useEffect } from "react";
import { z } from "zod";
import { BusinessEntityCard } from "~/components/BusinessEntity/BusinessEntityCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { ProductCard } from "~/components/ProductCard";
import { graphql } from "~/gql/client";
import { createTermDepositApprovalTypeValues } from "~/gql/client/enumValues";
import type { AssetCardFieldsFragment, Currency } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const organisationsQuery = graphql(`
  query NewTermDepositFormOrganisations {
    organisations {
      id
      name
      ...OrganisationCardFields
      transferApprovalPolicy {
        id
        userReviewRequired
      }
      businessEntities {
        id
        name
        ...BusinessEntityCardFields
      }
    }
  }
`);

const businessEntityDetailsQuery = graphql(`
  query NewTermDepositFormBusinessEntity($businessEntityId: ID!) {
    node(id: $businessEntityId) {
      id
      ... on BusinessEntity {
        id
        assets {
          id
          ...AssetCardFields
        }
      }
    }
    products {
      id
      ...ProductCardFields
      ... on TermDepositProduct {
        rateQuotes {
          id
          duration
          rate
        }
      }
    }
  }
`);

const createTermDepositMutation = graphql(`
  mutation NewTermDepositFormCreateTermDeposit(
    $input: CreateTermDepositInput!
  ) {
    createTermDeposit(input: $input) {
      termDepositAsset {
        id
      }
    }
  }
`);

export const Route = createFileRoute("/__auth/term-deposits_/new")({
  head: () => opsHead("New Term Deposit"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const formSchema = z.object({
  approvalType: z.enum(createTermDepositApprovalTypeValues),
  organisationId: z.string().min(1),
  businessEntityId: z.string().min(1),
  productId: z.string().min(1),
  durationDays: z.string().min(1, "Required"),
  apy: decimalSchema.refine((value) => value.gt(0), "Required"),
  principalAmount: decimalSchema.refine((value) => value.gt(0), "Required"),
  currency: currencySchema,
  identifier: z.string().optional(),
  startDate: dateSchema
    .refine(
      (date) => date >= now().minus({ days: 90 }).toJSDate(),
      "Start date cannot be backdated by more than 90 days",
    )
    .optional(),
  fundsSource: z.record(
    z.string(),
    z
      .object({
        checked: z.boolean().optional(),
        amount: z.number().optional(),
      })
      .optional(),
  ),
});

type FormValues = z.input<typeof formSchema>;

function isPositiveFundsAllocation(
  entry: [string, { checked?: boolean; amount?: number } | undefined],
): boolean {
  const [, allocation] = entry;
  return Boolean(allocation?.checked && allocation.amount);
}

const defaultValues: FormValues = {
  approvalType: "REQUIRED",
  organisationId: "",
  businessEntityId: "",
  productId: "",
  durationDays: "",
  apy: new Decimal(0),
  principalAmount: new Decimal(0),
  currency: "AUD" as Currency,
  identifier: "",
  fundsSource: {},
};

type FundsAllocation = { checked?: boolean; amount?: number };

function FundingList({
  sourceAssets,
  value,
  targetAmount,
  onValueChange,
}: {
  sourceAssets: AssetCardFieldsFragment[];
  value: Record<string, FundsAllocation | undefined>;
  targetAmount: Decimal;
  onValueChange: (value: Record<string, FundsAllocation | undefined>) => void;
}) {
  const tally = Object.values(value).reduce(
    (acc, allocation) =>
      acc.plus(allocation?.checked ? (allocation?.amount ?? 0) : 0),
    new Decimal(0),
  );
  const remaining = Decimal.max(0, targetAmount.minus(tally));

  const setChecked = (id: string, checked: boolean, balance?: Decimal) => {
    onValueChange({
      ...value,
      [id]: checked
        ? {
            checked: true,
            amount: Decimal.min(remaining, balance ?? remaining).toNumber(),
          }
        : { checked: false, amount: undefined },
    });
  };

  const renderAmountInput = (id: string, currency: Currency) => (
    <CurrencyAmountInput
      rootClassName="w-[23ch]"
      visuallyDisabled={!value[id]?.checked}
      allowLeadingZeros
      decimalScale={2}
      align="end"
      onClick={() => {
        if (!value[id]?.checked) {
          setChecked(id, true);
        }
      }}
      value={
        value[id]?.amount != null ? new Decimal(value[id]?.amount ?? 0) : null
      }
      onValueChange={(amount) =>
        onValueChange({
          ...value,
          [id]: { ...value[id], amount: amount?.toNumber() },
        })
      }
      currency={currency}
    />
  );

  const checklistValue = Object.fromEntries(
    Object.entries(value).map(([id, allocation]) => [
      id,
      { checked: allocation?.checked ?? false },
    ]),
  );

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between text-sm">
        <Txt variant="description">
          {remaining.gt(0) ? "Remaining to reach target" : "Target reached"}
        </Txt>
        <CurrencyAmountUI nonCopyable amount={remaining} currency="AUD" />
      </div>
      <CheckableList
        value={checklistValue}
        onValueChange={(newValue) => {
          const changedEntry = Object.entries(newValue).find(
            ([id, item]) => item.checked !== (value[id]?.checked ?? false),
          );
          if (!changedEntry) {
            return;
          }
          const [id, item] = changedEntry;
          const asset = sourceAssets.find((a) => a.id === id);
          setChecked(id, item.checked ?? false, asset?.estimatedValue?.amount);
        }}
      >
        <CheckableListItem
          value="external"
          leftTitle="Deposit from external account"
          rightContent={renderAmountInput("external", "AUD" as Currency)}
        />
        {sourceAssets.map((asset) => (
          <CheckableListItem
            key={asset.id}
            value={asset.id}
            leftTitle={
              <AccountCard
                account={asset}
                subText="BALANCE"
                hideTdProgress
                hideTdBadge
              />
            }
            rightContent={renderAmountInput(asset.id, asset.currency)}
          />
        ))}
      </CheckableList>
    </div>
  );
}

function View() {
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(organisationsQuery);
  const [createTermDeposit] = useMutation(createTermDepositMutation);
  const organisations = data.organisations ?? [];

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating term deposit",
        });
        return;
      }

      const funding = Object.entries(parsed.data.fundsSource)
        .filter(isPositiveFundsAllocation)
        .map(([sourceKey, allocation]) => ({
          sourceAssetId: sourceKey === "external" ? null : sourceKey,
          amount: new Decimal(allocation?.amount ?? 0),
        }))
        .filter((entry) => entry.amount.gt(0));

      try {
        const result = await createTermDeposit({
          variables: {
            input: {
              organisationId: parsed.data.organisationId,
              businessEntityId: parsed.data.businessEntityId,
              productId: parsed.data.productId,
              principalAmount: parsed.data.principalAmount,
              apy: parsed.data.apy,
              durationDays: Number(parsed.data.durationDays),
              startDate: parsed.data.startDate ?? null,
              currency: parsed.data.currency,
              rolloverFromAssetId: null,
              funding,
              approvalType: parsed.data.approvalType,
            },
          },
        });

        const createdId = result.data?.createTermDeposit.termDepositAsset.id;
        if (!createdId) {
          throw new Error("No term deposit returned");
        }

        toast({
          kind: "success",
          autoHide: true,
          title: "Created new term deposit",
        });
        void navigate({
          to: "/assets/$assetId",
          params: { assetId: createdId },
        });
      } catch {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to create term deposit",
        });
      }
    },
  });

  const organisationId = useStore(
    form.store,
    (state) => state.values.organisationId,
  );
  const businessEntityId = useStore(
    form.store,
    (state) => state.values.businessEntityId,
  );
  const productId = useStore(form.store, (state) => state.values.productId);
  const principalAmount = useStore(
    form.store,
    (state) => state.values.principalAmount,
  );

  const selectedOrganisation = organisations.find(
    (o) => o.id === organisationId,
  );

  useEffect(() => {
    if (selectedOrganisation?.businessEntities?.length === 1) {
      form.setFieldValue(
        "businessEntityId",
        selectedOrganisation.businessEntities[0].id,
      );
    }
  }, [form, selectedOrganisation]);

  const { data: businessEntityData, loading: businessEntityLoading } = useQuery(
    businessEntityDetailsQuery,
    {
      variables: { businessEntityId: businessEntityId ?? "" },
      skip: !businessEntityId,
    },
  );

  const businessEntity =
    businessEntityData?.node?.__typename === "BusinessEntity"
      ? businessEntityData.node
      : undefined;

  const termDepositProducts = (businessEntityData?.products ?? []).filter(
    (product) => product.__typename === "TermDepositProduct",
  );

  const selectedProduct = termDepositProducts.find((p) => p.id === productId);

  const productRateQuotes = (selectedProduct?.rateQuotes ?? [])
    .map((rateQuote) => ({
      id: rateQuote.id,
      durationDays: Math.round(
        typeof rateQuote.duration === "string"
          ? Duration.fromISO(rateQuote.duration).as("days")
          : rateQuote.duration.as("days"),
      ),
      rate: rateQuote.rate,
    }))
    .sort((a, b) => a.durationDays - b.durationDays);

  const sourceAssets = (businessEntity?.assets ?? []).filter(
    (asset) =>
      asset.assetKind !== "CLIENT_MANAGED" || asset.class === "TERM_DEPOSIT",
  );

  let businessEntitySection: React.ReactNode = null;
  if (businessEntityId && (businessEntityLoading || !businessEntity)) {
    businessEntitySection = <Spinner />;
  } else if (businessEntityId && businessEntity) {
    businessEntitySection = (
      <>
        <section className="grid gap-4">
          <Txt variant="section-title">Rate & Principal</Txt>
          <div className="grid grid-flow-col gap-4">
            <form.AppField name="productId">
              {(field) => (
                <field.SelectField
                  label="Provider"
                  placeholder="Select a provider..."
                  onValueChange={(value) => {
                    field.handleChange(value);
                    form.setFieldValue("durationDays", "");
                    form.setFieldValue("apy", new Decimal(0));
                  }}
                >
                  {termDepositProducts
                    .slice()
                    .sort((a, b) =>
                      a.internalName.localeCompare(b.internalName),
                    )
                    .map((product) => (
                      <field.SelectItem key={product.id} value={product.id}>
                        <ProductCard product={product} />
                      </field.SelectItem>
                    ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="durationDays">
              {(field) => (
                <field.SelectField
                  label="Term"
                  placeholder={
                    selectedProduct
                      ? "Select a term..."
                      : "Select a provider first"
                  }
                  disabled={!selectedProduct}
                  onValueChange={(value) => {
                    field.handleChange(value);
                    const matchingQuote = productRateQuotes.find(
                      (quote) => String(quote.durationDays) === value,
                    );
                    form.setFieldValue(
                      "apy",
                      matchingQuote
                        ? matchingQuote.rate.div(100)
                        : new Decimal(0),
                    );
                  }}
                >
                  {productRateQuotes.map((quote) => (
                    <field.SelectItem
                      key={quote.id}
                      value={String(quote.durationDays)}
                    >
                      {quote.durationDays} days · {quote.rate.toFixed(2)}% p.a.
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>
          </div>

          <div className="grid grid-flow-col gap-4">
            <form.AppField name="apy">
              {(field) => (
                <field.PercentageField
                  label="Rate"
                  scale="0-1"
                  decimalScale={2}
                />
              )}
            </form.AppField>
            <form.AppField name="principalAmount">
              {(field) => (
                <field.CurrencyAmountField
                  label="Principal Amount"
                  currency="AUD"
                />
              )}
            </form.AppField>
          </div>
        </section>

        <section className="grid gap-4">
          <Txt variant="section-title">Funding</Txt>
          <form.AppField name="fundsSource">
            {(field) => (
              <FundingList
                sourceAssets={sourceAssets}
                value={field.state.value ?? {}}
                targetAmount={principalAmount ?? new Decimal(0)}
                onValueChange={field.handleChange}
              />
            )}
          </form.AppField>
        </section>

        <section className="grid gap-4">
          <Txt variant="section-title">Configuration</Txt>
          <div className="grid grid-flow-col gap-4">
            <form.AppField name="identifier">
              {(field) => <field.InputField label="Identifier" />}
            </form.AppField>
            <form.AppField name="startDate">
              {(field) => <field.DatePickerField label="Start Date" />}
            </form.AppField>
          </div>
          <div className="flex flex-col gap-2">
            <form.AppField name="approvalType">
              {(field) => (
                <field.SelectField label="Bypass approvals?">
                  <field.SelectItem value="REQUIRED">
                    Do not bypass approval policy
                  </field.SelectItem>
                  <field.SelectItem value="BYPASS">
                    Bypass approval policy
                  </field.SelectItem>
                </field.SelectField>
              )}
            </form.AppField>
            {selectedOrganisation && (
              <div>
                {selectedOrganisation.transferApprovalPolicy
                  ?.userReviewRequired ? (
                  <Txt variant="body" tone="danger">
                    This organisation currently requires approvals.
                  </Txt>
                ) : (
                  <Txt variant="description">
                    This organisation does not currently require approvals.
                  </Txt>
                )}
              </div>
            )}
          </div>
        </section>

        <div className="flex justify-end">
          <form.SubmitButton childrenWhileSubmitting="Creating...">
            Create Term Deposit
          </form.SubmitButton>
        </div>
      </>
    );
  }

  return (
    <AdminPageContainer testId="admin-new-term-deposit">
      <AdminPageHeader
        title="New Term Deposit"
        subtitle="Create a new term deposit for an organisation and business entity"
      />
      <form.AppForm>
        <form
          className="grid max-w-3xl gap-10 pb-10"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <section className="grid gap-4">
            <Txt variant="section-title">Organisation & Business Entity</Txt>
            <div className="grid grid-flow-col gap-4">
              <form.AppField name="organisationId">
                {(field) => (
                  <field.SelectField
                    label="Organisation"
                    placeholder="Select an organisation..."
                    onValueChange={(value) => {
                      field.handleChange(value);
                      form.setFieldValue("businessEntityId", "");
                    }}
                  >
                    {organisations
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((o) => (
                        <field.SelectItem key={o.id} value={o.id}>
                          <OrganisationCard organisation={o} />
                        </field.SelectItem>
                      ))}
                  </field.SelectField>
                )}
              </form.AppField>

              <form.AppField name="businessEntityId">
                {(field) => (
                  <field.SelectField
                    label="Business Entity"
                    placeholder={
                      selectedOrganisation
                        ? "Select a business entity..."
                        : "Select organisation first"
                    }
                    disabled={!selectedOrganisation}
                  >
                    {(selectedOrganisation?.businessEntities ?? [])
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((be) => (
                        <field.SelectItem key={be.id} value={be.id}>
                          <BusinessEntityCard businessEntity={be} />
                        </field.SelectItem>
                      ))}
                  </field.SelectField>
                )}
              </form.AppField>
            </div>
          </section>

          {businessEntitySection}
        </form>
      </form.AppForm>
    </AdminPageContainer>
  );
}
